import React, { useEffect } from "react";
import { ErrorMessage, Field, useField, useFormikContext } from "formik";
import { useTranslation } from "react-i18next";

interface NumberInputProps {
    id: string;
    name: string;
    placeholder: string;
    disabled?: boolean;
    maxLength?: number;
    isRadioButton?: boolean;
    icon?: React.ReactNode;
    combined?: boolean;
    quantityWarnings?: boolean;
    allowZero?: boolean;
    validateImmediately?: boolean;
}

const NumberInput: React.FC<NumberInputProps> = ({
    id,
    name,
    placeholder,
    disabled,
    maxLength = 20,
    isRadioButton = false,
    icon,
    combined,
    quantityWarnings,
    allowZero = false,
    validateImmediately = false,
}) => {
    const { t } = useTranslation();
    const [field, meta, helpers] = useField(name);
    const { setFieldValue, validateField } = useFormikContext();
    const errorClass =
        meta.touched && meta.error
            ? isRadioButton
                ? "border-danger"
                : "is-invalid padding-placeholder-is-invalid"
            : "";

    const warningsClass = quantityWarnings ? "border-orange shadow-none" : "";

    const inputClassName = `form-control fs-7 ${combined ? "rounded-end-0" : ""}
   ${isRadioButton ? "ps-7" : ""} ${quantityWarnings ? warningsClass : errorClass}`;

    useEffect(() => {
        if (field.value === null || field.value === undefined) {
            helpers.setValue("");
        }
    }, [field.value, helpers]);

    const isValidInteger = (value: string): boolean => {
        if (allowZero) {
            const regex = /^(0|[1-9]\d*)$/;
            return regex.test(value);
        } else {
            const regex = /^[1-9]\d*$/;
            return regex.test(value);
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        if (value === "") {
            helpers.setValue("");
            if (combined) {
                const manualFieldName = name.replace(/\.quantity$/, ".isAmountManuallyEdited");
                setFieldValue(manualFieldName, false);
            }
            return;
        }
        if (isValidInteger(value)) {
            helpers.setValue(Number(value));
            if (combined) {
                const manualFieldName = name.replace(/\.quantity$/, ".isAmountManuallyEdited");
                setFieldValue(manualFieldName, false);
            }

            if (validateImmediately) {
                helpers.setTouched(true);
                setTimeout(() => {
                    validateField(name);
                }, 0);
            }
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        const invalidKeys = ["e", "E", "+", "-", ".", ",", " "];
        if (invalidKeys.includes(e.key)) {
            e.preventDefault();
        }
    };

    return (
        <div className="d-flex flex-column w-100">
            <div className="position-relative">
                <Field
                    name={name}
                    id={id}
                    placeholder={t(placeholder)}
                    className={inputClassName}
                    value={field.value ?? ""}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                    disabled={disabled}
                    maxLength={maxLength}
                />
            </div>
            <i
                className={`position-absolute ${icon ? "d-block" : "d-none"} fs-7  end-0 top-50 translate-middle-y text-dark pe-6`}
            >
                {icon}
            </i>
            {!isRadioButton ? (
                <ErrorMessage name={name}>
                    {(msg) => <div className="invalid-feedback d-block w-100">{msg}</div>}
                </ErrorMessage>
            ) : null}
        </div>
    );
};

export default NumberInput;
